// @figma file=pkCGby54zXzTsccXvHqzO6 node=3830:2149 name="Check Box" set="mytaible-shadcn-studio" verified=2026-03-21

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  /**
   * The checked state of the checkbox.
   * `true` → checked, `false` → unchecked, `'indeterminate'` → partial selection.
   * Controlled by the consumer; use `onCheckedChange` to update.
   *
   * Figma: State=Checked / State=Unchecked / State=Indeterminate
   */
  checked?: boolean | 'indeterminate';
  /** Callback fired when the checked state changes. */
  onCheckedChange?: (checked: boolean | 'indeterminate') => void;
  /**
   * When true the checkbox is visually muted and non-interactive.
   * Figma: State=Disabled Checked / State=Disabled Uncheck
   */
  disabled?: boolean;
  /** Marks this field as required in a form context (passed to the hidden input). */
  required?: boolean;
  /** Name forwarded to the hidden form input. */
  name?: string;
  /** Value forwarded to the hidden form input. */
  value?: string;
  /** Additional CSS classes merged onto the root element. */
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Binary or indeterminate selection control built on Radix `CheckboxPrimitive`.
 *
 * Supports all three Figma states — Unchecked, Checked, Indeterminate — and
 * the disabled modifier.  Keyboard accessible out of the box (Space to toggle).
 *
 * @example
 * // Uncontrolled — simple boolean
 * <Checkbox id="agree" />
 *
 * @example
 * // Controlled
 * const [checked, setChecked] = React.useState<boolean | 'indeterminate'>(false);
 * <Checkbox checked={checked} onCheckedChange={setChecked} />
 *
 * @example
 * // Indeterminate (select-all pattern)
 * <Checkbox checked="indeterminate" aria-label="Select all rows" />
 */
const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, ...props }, ref) => (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        // ── Layout & shape ──────────────────────────────────────────────────────
        'peer h-4 w-4 shrink-0 rounded-[var(--field-radius,var(--radius))]',
        // ── Border (unchecked) — Figma: 1px solid border-border ─────────────────
        'border border-border shadow-sm',
        // ── Focus ring ──────────────────────────────────────────────────────────
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        // ── Checked / Indeterminate — Figma: bg-primary, indicator white ─────────
        'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-transparent',
        'data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=indeterminate]:border-transparent',
        // ── Disabled — Figma: State=Disabled Checked / Disabled Uncheck ─────────
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
        {props.checked === 'indeterminate' ? (
          <Minus className="h-3 w-3" aria-hidden="true" />
        ) : (
          <Check className="h-3 w-3" aria-hidden="true" />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  ),
);
Checkbox.displayName = 'Checkbox';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Checkbox };
